import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { gbp } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { BadgePercent, Loader2, RotateCcw, Sparkles } from "lucide-react";
import DealCard from "@/components/DealCard";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "subs", label: "Subscriptions" },
  { id: "fin", label: "Money products" },
];

export default function BetterDeals({ reloadKey }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => api.get("/better-deals").then(({ data }) => setData(data)).catch(() => setData({ deals: [], error: true })), []);
  useEffect(() => { load(); }, [load, reloadKey]);

  const dismiss = async (deal) => {
    setData((d) => ({ ...d, deals: d.deals.filter((x) => x.id !== deal.id), hidden_count: (d.hidden_count || 0) + 1 }));
    try {
      await api.post(`/better-deals/${encodeURIComponent(deal.id)}/dismiss`);
      toast.success("Deal hidden");
    } catch {
      toast.error("Couldn't hide that deal");
      load();
    }
  };

  const restore = async () => {
    try {
      await api.post("/better-deals/restore");
      setData(null);
      load();
    } catch {
      toast.error("Couldn't restore hidden deals");
    }
  };

  const deals = (data?.deals || []).filter((d) => filter === "all" || (filter === "fin" ? d.is_financial : !d.is_financial));
  const monthly = deals.reduce((s, d) => s + d.monthly_saving, 0);

  return (
    <div className="mm-glow-card p-6" data-testid="better-deals">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <span className="h-10 w-10 rounded-xl bg-[#00C896]/15 flex items-center justify-center text-[#00C896]"><BadgePercent className="h-5 w-5" /></span>
          <div>
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#00C896]">Better deals</p>
            <h3 className="font-display text-lg font-semibold">Pay less for what you already use</h3>
          </div>
        </div>
        {data?.deals?.length > 0 && (
          <div className="text-left sm:text-right">
            <p className="text-[11px] text-slate-500">Potential saving{filter !== "all" ? " (filtered)" : ""}</p>
            <p data-testid="better-deals-total" className="font-num text-2xl font-extrabold text-[#00C896]">{gbp(monthly)}<span className="text-sm text-slate-400 font-sans font-normal"> / month</span></p>
          </div>
        )}
      </div>

      {data === null ? (
        <div data-testid="better-deals-loading" className="mt-5 rounded-xl border border-white/8 bg-[#0D0D0D] p-8 flex items-center justify-center gap-3 text-sm text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin text-[#00C896]" /> Comparing your subscriptions and accounts with UK deals…
        </div>
      ) : data.deals.length === 0 ? (
        <div data-testid="better-deals-empty" className="mt-5 rounded-xl border border-dashed border-white/12 p-8 text-center">
          <p className="text-sm text-slate-400">{data.error ? "Couldn't load deals right now. Please try again later." : "No better deals to show yet. We need a couple of months of transactions to compare fairly, so check back soon."}</p>
        </div>
      ) : (
        <>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {FILTERS.map((f) => (
              <button key={f.id} data-testid={`better-deals-filter-${f.id}`} onClick={() => setFilter(f.id)}
                className={`rounded-full px-3 py-1 text-xs border transition-colors ${filter === f.id ? "border-[#00C896]/40 bg-[#00C896]/15 text-[#4DE3BF]" : "border-white/10 text-slate-400 hover:text-white"}`}>{f.label}</button>
            ))}
            {data.ai_explanations && <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-500"><Sparkles className="h-3 w-3 text-[#00C896]" /> Explained by AI from your transactions</span>}
          </div>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
            {deals.map((d) => <DealCard key={d.id} deal={d} onDismiss={dismiss} />)}
          </div>
          {deals.length === 0 && <p className="mt-4 text-sm text-slate-500 text-center py-4">No deals in this group.</p>}
        </>
      )}

      {data && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
          <span data-testid="better-deals-checked">Prices and rates checked {data.rates_checked}. Always confirm on the provider's site.</span>
          {data.hidden_count > 0 && (
            <Button data-testid="better-deals-restore" onClick={restore} variant="ghost" size="sm" className="h-7 px-2 text-[11px] text-slate-400 hover:text-white hover:bg-white/5">
              <RotateCcw className="h-3 w-3 mr-1" /> Show {data.hidden_count} hidden
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
